import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Eye, Trash2, User } from 'lucide-react';
import Modal from '../components/Common/Modal';
import CustomSelect from '../components/Common/CustomSelect';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import { Patient } from '../types';


const Patients: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);

  const [pForename, setPForename] = useState('');
  const [pSurname, setPSurname] = useState('');
  const [pNhsId, setPNhsId] = useState('');
  const [pDob, setPDob] = useState('');
  const [pGender, setPGender] = useState('');
  const [pHospitalNumber, setPHospitalNumber] = useState('');
  const [pMrnNumber, setPMrnNumber] = useState('');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);

  const navigate = useNavigate();

  const filteredPatients = patients.filter(patient => {
    const matchesSearch = 
      (patient.forename || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (patient.surname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (patient.nhs_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (patient.hospital_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const fetchPatients = async () => {
    try {
      let response;
      if (user?.role === ROLES.DOCTOR) {
        response = await api.get(`/patient/doctor/${user.id}`);
      } else {
        response = await api.get("/patient");
      }
      
      // Handle different response structures
      const patients = response.data?.data || response.data?.patients || response.data || [];
      setPatients(Array.isArray(patients) ? patients : []);
    } catch (error) {
      console.error("Error fetching patients:", error);
      toast.error("Failed to load patients");
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const getAge = (dobString: string ) => {
    if (!dobString) return "-";
  
    const dob = new Date(dobString);
    const today = new Date();
  
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
  
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
  
    return age;
  };
  

  // const getStatusColor = (status: string) => {
  //   switch (status.toLowerCase()) {
  //     case 'active':
  //       return 'bg-green-100 text-green-800';
  //     case 'monitoring':
  //       return 'bg-primary-100 text-primary-800';
  //     case 'completed':
  //       return 'bg-gray-100 text-gray-800';
  //     default:
  //       return 'bg-gray-100 text-gray-800';
  //   }
  // };

  const addPatient = async (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    
    try {
      const patientData = {
        forename: pForename,
        surname: pSurname,
        nhs_number: pNhsId,
        date_of_birth: pDob,
        gender: pGender,
        hospital_number: pHospitalNumber,
        mrn_number: pMrnNumber,
      };

      if (!patientData.forename || !patientData.surname || !patientData.nhs_number || !patientData.date_of_birth || !patientData.gender || !patientData.hospital_number || !patientData.mrn_number) {
        toast.error("All fields are required");
      }

      await api.post("/patient", patientData);

      await fetchPatients();
      setShowAddPatient(false);
      setPForename('');
      setPSurname('');
      setPNhsId('');
      setPDob('');
      setPGender('');
      setPHospitalNumber('');
      setPMrnNumber('');
    } catch (error) {
      console.error("Error adding patient:", error);
    }
  }

  const handleDeletePatient = (patientId: number) => {
    setSelectedPatientId(patientId);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedPatientId) return;
  
    try {
      await api.delete(`/patient/${selectedPatientId}`);
      await fetchPatients();
  
      setDeleteModalOpen(false);
      setSelectedPatientId(null);
    } catch (err) {
      console.error("Error deleting patient:", err);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patients</h1>
          <p className="text-gray-600">Manage patient records and clinical data</p>
        </div>
        <button
          onClick={() => setShowAddPatient(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Patient</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, NHS ID, or Hospital Number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          {/* <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="monitoring">diagnosis.conditionMonitoring</option>
              <option value="completed">Completed</option>
            </select>
          </div> */}
        </div>
      </div>


      {/* Patients Table */}
      {filteredPatients.length === 0 ? (
        <div className="card text-center py-12">
          <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Patients found</h3>
          <p className="text-gray-600">Try adjusting your search criteria or add a new Patient.</p>
        </div>
      ) : (
        <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  NHS ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hospital Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  MRN Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  gender
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  date of birth
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPatients.map((patient) => (
                <tr key={patient.patient_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-600">
                            {patient.forename?.[0]}{patient.surname?.[0]}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{patient.forename} {patient.surname}</div>
                        <div className="text-sm text-gray-500">{getAge(patient.date_of_birth)} years, {patient.gender}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {patient.nhs_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{patient.hospital_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {patient.mrn_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {patient.gender}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {patient.date_of_birth}
                  </td>
              
                  {/* <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{patient.diagnosis.condition}</div>
                    <div className="text-sm text-gray-500">{patient.diagnosis.subtype}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {patient.diagnosis.stage}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {patient.clinicalSummary.lastMdtReview}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(patient.clinicalSummary.status)}`}>
                      {patient.clinicalSummary.status}
                    </span>
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => {
                          if (!patient.patient_id) {
                            toast.error("Patient ID is missing");
                            console.error("Patient ID is missing for patient:", patient);
                            return;
                          }
                          navigate(`/patients/${patient.patient_id}`);
                        }}
                        className="text-primary-600 hover:text-primary-900 flex items-center space-x-1"
                      >
                        <Eye className="h-4 w-4" />
                        <span>View</span>
                      </button>
                      <button 
                        onClick={() => handleDeletePatient(patient.patient_id)}
                        className="text-red-600 hover:text-red-900 flex items-center space-x-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <Modal title="Add New Patient" isOpen={showAddPatient} onClose={() => setShowAddPatient(false)}>
        <form onSubmit={addPatient} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Forename</label>
              <input value={pForename} onChange={(e) => setPForename(e.target.value)} className="input-field mt-1" placeholder="Enter forename" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Surname</label>
              <input value={pSurname} onChange={(e) => setPSurname(e.target.value)} className="input-field mt-1" placeholder="Enter surname" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">NHS number</label>
              <input value={pNhsId} onChange={(e) => setPNhsId(e.target.value)} className="input-field mt-1" placeholder="Enter NHS number" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
              <input type="date" value={pDob} onChange={(e) => setPDob(e.target.value)} className="input-field mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Gender</label>
              <CustomSelect
                value={pGender}
                onChange={(val) => setPGender(val)}
                options={[
                  { value: '', label: 'Select gender' },
                  { value: 'Male', label: 'Male' },
                  { value: 'Female', label: 'Female' },
                  { value: 'Other', label: 'Other' },
                ]}
                placeholder="Select gender"
                className="mt-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Hospital number</label>
              <input value={pHospitalNumber} onChange={(e) => setPHospitalNumber(e.target.value)} className="input-field mt-1" placeholder="Enter hospital number" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">MRN number</label>
              <input value={pMrnNumber} onChange={(e) => setPMrnNumber(e.target.value)} className="input-field mt-1" placeholder="Enter MRN number" />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <button type="button" onClick={() => setShowAddPatient(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Add Patient</button>
          </div>
        </form>
      </Modal>

      <Modal title="Delete Patient" isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}
        widthClassName="max-w-md"
      >
        <p className="text-gray-700 mb-6">
          Are you sure you want to delete this patient? This action cannot be undone.
        </p>

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setDeleteModalOpen(false)}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >
            Cancel
          </button>

          <button
            onClick={confirmDelete}
            className="btn-danger"
          >
            Delete
          </button>
        </div>
      </Modal>

    </div>
  );
};

export default Patients;
